import { CAR_FINANCE_BACKEND_URL } from '../config/env'

const CACHE_TIMEOUT_MS = 3000 // 3 second timeout for verification fetch

// sessionStorage is scoped per-tab, so two tabs/windows opened against the same deeplink
// (e.g. a user double-clicking an email link, or opening it twice) share no state and both
// end up POSTing a fresh datastore record. localStorage is shared across all tabs of the
// same browser/device, so the second one can find and reuse the first's in-flight capture.
const store = window.localStorage

const LOCK_TIMEOUT_MS = 15000 // how long a capture may run before its lock is considered abandoned
const LOCK_POLL_INTERVAL_MS = 300
const LOCK_WAIT_MS = 5000 // how long a second caller waits for the first capture to finish

// --- Private Helpers ---

async function signPayload(uniqueID, payload) {
  try {
    // Import uniqueID as HMAC-SHA256 key
    const keyMaterial = new TextEncoder().encode(uniqueID)
    const key = await crypto.subtle.importKey(
      'raw',
      keyMaterial,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    )

    // Sign the payload
    const payloadString = JSON.stringify(payload)
    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      new TextEncoder().encode(payloadString)
    )

    // Convert signature to base64
    const signatureArray = new Uint8Array(signature)
    const signatureBase64 = btoa(String.fromCharCode(...signatureArray))

    return {
      ...payload,
      sig: signatureBase64,
    }
  } catch (error) {
    throw error
  }
}

async function verifyPayload(uniqueID, stored) {
  try {
    // Extract payload and signature
    const { sig, ...payload } = stored
    if (!sig) {
      return false
    }

    // Import uniqueID as HMAC-SHA256 key
    const keyMaterial = new TextEncoder().encode(uniqueID)
    const key = await crypto.subtle.importKey(
      'raw',
      keyMaterial,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['verify']
    )

    // Reconstruct expected signature
    const payloadString = JSON.stringify(payload)
    const signatureBytes = new Uint8Array(
      atob(sig)
        .split('')
        .map((c) => c.charCodeAt(0))
    )

    // Verify signature
    const isValid = await crypto.subtle.verify(
      'HMAC',
      key,
      signatureBytes,
      new TextEncoder().encode(payloadString)
    )

    return isValid
  } catch (error) {
    return false
  }
}

// --- Public API ---

export async function getSession(uniqueID) {
  if (!uniqueID) {
    return null
  }

  try {
    const cachedJson = store.getItem(`ip_capture_${uniqueID}`)
    if (!cachedJson) {
      return null
    }

    const cachedData = JSON.parse(cachedJson)

    // Verify signature before proceeding
    const isSignatureValid = await verifyPayload(uniqueID, cachedData)
    if (!isSignatureValid) {
      clearSession(uniqueID)
      return null
    }

    // Verify the cached data by fetching the datastore document
    const verifyController = new AbortController()
    const verifyTimeout = setTimeout(() => verifyController.abort(), CACHE_TIMEOUT_MS)

    try {
      const verifyRes = await fetch(`${CAR_FINANCE_BACKEND_URL}/api/1.0/datastore/${cachedData.dataid}`, {
        signal: verifyController.signal,
        headers: {
          'x-api-key': process.env.REACT_APP_DATASTORE_API_KEY,
        },
      })
      clearTimeout(verifyTimeout)

      if (verifyRes.ok) {
        const verifyData = await verifyRes.json()

        // Check if the stored identifier matches
        if (verifyData.uniqueID === uniqueID) {
          const result = { dataid: cachedData.dataid, ip: cachedData.ip };
          if (verifyData.journeySubmissionFingerprint) {
            result.journeySubmissionFingerprint = verifyData.journeySubmissionFingerprint;
          }
          return result;
        }
      }

      // Cache is invalid
      clearSession(uniqueID)
      return null
    } catch (verifyError) {
      clearTimeout(verifyTimeout)
      // Fetch failed or timed out - clear cache and return null
      clearSession(uniqueID)
      return null
    }
  } catch (cacheError) {
    return null
  }
}

export async function saveSession(uniqueID, { dataid, ip }) {
  if (!uniqueID || !dataid) {
    return
  }

  try {
    const signedPayload = await signPayload(uniqueID, { dataid, ip })
    store.setItem(`ip_capture_${uniqueID}`, JSON.stringify(signedPayload))
  } catch (cacheStoreError) {
  }
}

export function clearSession(uniqueID) {
  if (!uniqueID) {
    return
  }

  try {
    store.removeItem(`ip_capture_${uniqueID}`)
  } catch (clearError) {
  }
}

// --- Capture lock ---
// Closes the race where two near-simultaneous loads (two tabs, a reload before the first
// capture's response lands, an email security scanner pre-fetching the link, etc.) both
// check getSession(), both find nothing yet, and both POST a new datastore record. The
// first caller claims a short-lived lock; a second caller sees it and waits for the first
// to finish and save its session instead of capturing independently.

export function acquireCaptureLock(uniqueID) {
  if (!uniqueID) {
    return true
  }

  try {
    const key = `ip_capture_lock_${uniqueID}`
    const existingRaw = store.getItem(key)
    if (existingRaw) {
      const existing = JSON.parse(existingRaw)
      if (Date.now() - existing.startedAt < LOCK_TIMEOUT_MS) {
        return false // another capture is already in flight
      }
      // Stale lock (previous attempt crashed/abandoned) - safe to take over
    }

    store.setItem(key, JSON.stringify({ startedAt: Date.now() }))
    return true
  } catch (lockError) {
    return true // storage unavailable - proceed rather than block submission
  }
}

export function releaseCaptureLock(uniqueID) {
  if (!uniqueID) {
    return
  }

  try {
    store.removeItem(`ip_capture_lock_${uniqueID}`)
  } catch (releaseError) {
  }
}

// Polls for the other caller's session to appear after losing the lock race. Falls back to
// null (letting the caller capture independently) if it doesn't show up within LOCK_WAIT_MS -
// duplicate-but-successful beats blocked-and-failed.
export async function waitForSession(uniqueID) {
  const deadline = Date.now() + LOCK_WAIT_MS

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, LOCK_POLL_INTERVAL_MS))
    const session = await getSession(uniqueID)
    if (session) {
      return session
    }
  }

  return null
}
